import React, { useEffect, useState, useRef } from 'react'
import { useTour } from './hooks/useTour'
import { getElement, getRect } from './utils/tourHelpers'
import { Icon, Button } from 'antd'

const TourTooltip = () => {
    const {
        isTourActive,
        currentStep,
        nextStep,
        prevStep,
        endTour,
        currentStepIndex,
        totalSteps,
        isNextDisabled,
        pauseTour
    } = useTour()

    const [style, setStyle] = useState({})
    const [placement, setPlacement] = useState('bottom')
    const tooltipRef = useRef(null)

    const updatePosition = () => {
        if (!isTourActive || !currentStep) return

        const targetEl = getElement(currentStep.target)
        const tooltipEl = tooltipRef.current

        if (targetEl && tooltipEl) {
            const targetRect = getRect(targetEl)
            const tooltipRect = getRect(tooltipEl)
            const spacing = 15

            let top, left
            let newPlacement = currentStep.placement || 'bottom'

            // Calculate position based on placement
            switch (newPlacement) {
                case 'top':
                    top = targetRect.top - tooltipRect.height - spacing
                    left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2)
                    break
                case 'bottom':
                    top = targetRect.bottom + spacing
                    left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2)
                    break
                case 'left':
                    top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2)
                    left = targetRect.left - tooltipRect.width - spacing
                    break
                case 'right':
                    top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2)
                    left = targetRect.right + spacing
                    break
                default: // center or fallback
                    top = (window.innerHeight / 2) - (tooltipRect.height / 2)
                    left = (window.innerWidth / 2) - (tooltipRect.width / 2)
            }

            // Boundary checks to keep tooltip in viewport
            if (left < 10) left = 10
            if (left + tooltipRect.width > window.innerWidth - 10) {
                left = window.innerWidth - tooltipRect.width - 10
            }
            if (top < 10) top = 10
            if (top + tooltipRect.height > window.innerHeight - 10) {
                top = window.innerHeight - tooltipRect.height - 10
            }

            setStyle({ top, left })
            setPlacement(newPlacement)
        } else if (!targetEl) {
            // Center if no target
            const tooltipRect = tooltipEl ? getRect(tooltipEl) : { width: 350, height: 200 }
            setStyle({
                top: (window.innerHeight / 2) - (tooltipRect.height / 2),
                left: (window.innerWidth / 2) - (tooltipRect.width / 2)
            })
            setPlacement('center')
        }
    }

    useEffect(() => {
        if (isTourActive) {
            // Small delay to ensure DOM is ready and layout is stable
            setTimeout(updatePosition, 100)
            window.addEventListener('resize', updatePosition)
            window.addEventListener('scroll', updatePosition, true)
        }
        return () => {
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
        }
    }, [isTourActive, currentStep])

    if (!isTourActive || !currentStep) return null

    const isLastStep = currentStepIndex === totalSteps - 1

    return (
        <div
            ref={tooltipRef}
            className={`tour-tooltip placement-${placement} ${isTourActive ? 'visible' : ''}`}
            style={style}
        >
            <div className="tour-tooltip-header">
                <h3>{currentStep.title}</h3>
                <button className="tour-close-btn" onClick={endTour}>
                    <Icon type="close" />
                </button>
            </div>

            <div className="tour-tooltip-body">
                {currentStep.content}
            </div>

            <div className="tour-footer">
                <div className="tour-progress">
                    {currentStepIndex + 1} of {totalSteps}
                </div>
                <div className="tour-buttons">
                    {currentStepIndex > 0 && (
                        <Button size="small" onClick={prevStep}>
                            Prev
                        </Button>
                    )}
                    <Button
                        type="primary"
                        size="small"
                        onClick={isLastStep ? endTour : nextStep}
                        disabled={!isLastStep && isNextDisabled}
                    >
                        {isLastStep ? 'Finish' : 'Next'}
                    </Button>
                </div>
            </div>
            <div className="tour-arrow" />
        </div>
    )
}

export default TourTooltip
